import { datahubAxiosClient, ENDPOINT_DETAILS, INVITE_USER_ENDPOINT } from 'constants/server/apiEndPoint';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import appRoutes from 'app.routes';
import { ApiGetListParams } from 'types/apiParamsTypes';
import { IInvitationDto, IUserBranchDto, IUserBranchWithStatusDto } from './dtos';
import { AUTH_REDIRECT_URL, HEADER_USER_ID_KEY } from 'api/http';
import { ICommonResponse, StorageHeaderValueType } from 'api/types';
import { useLocalization } from 'localization';
import notifyHandler from 'utils/notifyHandler';
import { IAddNewUserParams, IUpdateProfileParams } from './models';
import { UserStatuses } from '../../constants/enums/status';
import UsersApi from './index';

export const userQueryKey = {
  all: ['user'] as const,
  filterUsers: ['filterUsers'] as const,
  list: (filters: ApiGetListParams) => [...userQueryKey.all, { filters }],
  userAccesses: ['userAccesses'] as const,
};

export const getUsersByBranchIdApi = (branchId: string) => {
  const { data, isLoading } = useQuery<IUserBranchDto[]>({
    queryKey: userQueryKey.list({ searchString: branchId }),
    queryFn: async ({ signal }) => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.searchUserByBranchId(branchId), { signal });
      return response.data;
    },
    enabled: !!branchId,
    staleTime: 0,
  });
  return { data, isLoading };
};

export const getUserAccessesAsync = ({
  branchId,
  userStatuses,
  keyword,
}: {
  branchId: string;
  userStatuses: string[];
  keyword?: string;
}) => {
  const { data, isLoading, isFetching, isRefetching, refetch } = useQuery<IUserBranchWithStatusDto[]>({
    queryKey: userQueryKey['userAccesses'],
    queryFn: async () => {
      const getUserPromises: Promise<IUserBranchWithStatusDto[]>[] = [];

      let actualUserStatuses = [...userStatuses];
      if (actualUserStatuses.length < 1) {
        actualUserStatuses = [UserStatuses.PENDING, UserStatuses.ACTIVE];
      }

      if (actualUserStatuses.indexOf(UserStatuses.PENDING) !== -1) {
        const getPendingUserPromise = datahubAxiosClient
          .get<IInvitationDto[]>(ENDPOINT_DETAILS.getPendingUsersByBranchId(branchId))
          .then(httpResponse => {
            const invitations = httpResponse.data;
            return invitations.map(
              invitation =>
                ({
                  id: invitation.id,
                  email: invitation.email,
                  isAdmin: false,
                  status: UserStatuses.PENDING,
                }) as IUserBranchWithStatusDto
            );
          })
          .catch(() => []);
        getUserPromises.push(getPendingUserPromise);
      }

      if (actualUserStatuses.indexOf(UserStatuses.ACTIVE) !== -1) {
        const getActiveUsersPromise = datahubAxiosClient
          .get(ENDPOINT_DETAILS.getActiveUsersByBranchId(branchId))
          .then(httpResponse => {
            const items = httpResponse.data;
            return items.map(
              (item: IUserBranchWithStatusDto) =>
                ({
                  ...item,
                  status: UserStatuses.ACTIVE,
                }) as IUserBranchWithStatusDto
            );
          })
          .catch(() => []);
        getUserPromises.push(getActiveUsersPromise);
      }

      if (getUserPromises.length < 1) {
        return [] as IUserBranchWithStatusDto[];
      }
      const getUsersResults = await Promise.all(getUserPromises);
      let users = getUsersResults.reduce((a, b) => a.concat(b));
      const trimmedKeyword = keyword?.trim();
      if (trimmedKeyword && trimmedKeyword.length > 0) {
        users = users.filter(x => x.email.indexOf(trimmedKeyword) !== -1);
      }
      return users;
    },
    enabled: branchId?.length > 0,
    staleTime: 0,
  });
  return { data, isLoading, isFetching, isRefetching, refetch };
};

export const authenticateUserMutation = () => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: async (customerId: string) => {
      // update header value to perform actions for the selected user
      const userIdHeader: StorageHeaderValueType = JSON.parse(localStorage.getItem(HEADER_USER_ID_KEY) || '');
      userIdHeader.value = customerId;
      localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify(userIdHeader));

      const redirectUrl = localStorage.getItem(AUTH_REDIRECT_URL);
      localStorage.removeItem(AUTH_REDIRECT_URL);
      navigate(redirectUrl || appRoutes.dashboard());
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('auth.pages.activeDirectory.errorGettingUser') });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey.all });
    },
  });
  return { mutateAsync, isLoading };
};

export const updateUserMutation = () => {
  return useMutation<
    ICommonResponse,
    unknown,
    {
      userId: string;
      payload: IUpdateProfileParams & { branchId?: string; isAdmin?: boolean };
      signal?: AbortSignal;
    }
  >({
    mutationFn: async ({ userId, payload, signal }) => {
      const { branchId, isAdmin } = payload;
      const updateUserPromises: Promise<void>[] = [];

      if (branchId && isAdmin !== undefined && isAdmin !== null) {
        const updateUserAccessPromise = UsersApi.updateBranchAccessForUser({
          userId,
          isAdmin,
          branchId,
        }).then(() => void 0);
        updateUserPromises.push(updateUserAccessPromise);
      }

      const updateUserDetailPromise = datahubAxiosClient.patch<ICommonResponse>(
        ENDPOINT_DETAILS.updateUser(userId),
        { ...payload },
        { signal }
      );
      updateUserPromises.push(updateUserDetailPromise.then(() => void 0));

      await Promise.all(updateUserPromises);
      return (await updateUserDetailPromise).data;
    },
  });
};

export const inviteUserMutation = () => {
  return useMutation<
    ICommonResponse,
    unknown,
    {
      payload: IAddNewUserParams;
      signal?: AbortSignal;
    }
  >({
    mutationFn: async ({ payload, signal }) => {
      // Pass the signal into axios config
      const response = await datahubAxiosClient.post<ICommonResponse>(INVITE_USER_ENDPOINT, { ...payload }, { signal });
      return response.data;
    },
  });
};

export const resendInvitationMutation = () => {
  return useMutation<
    ICommonResponse,
    unknown,
    {
      invitationId: string;
      signal?: AbortSignal;
    }
  >({
    mutationFn: async ({ invitationId, signal }) => {
      // Pass the signal into axios config
      const response = await datahubAxiosClient.post<ICommonResponse>(
        ENDPOINT_DETAILS.resendInvitation(invitationId),
        {},
        { signal }
      );
      return response.data;
    },
  });
};
